/* A usable name out of an email address, so a blank name field never costs us
   an account.

   Both signup forms used to refuse outright: "Please tell us your name." In the
   first 24 hours the block was instrumented it fired 13 times across 9
   visitors, 8 of them on this exact field, and not one of those 9 ever came
   back and finished. A day's signups, thrown away over a field we can fill in
   ourselves.

   The name is not load-bearing. It greets them on the dashboard and it goes on
   a posting as the poster's name — both of which are editable in the profile,
   and neither of which is worth a lost account. The email is the only thing
   registration genuinely cannot proceed without, because it IS the identity.

   Conservative on purpose. "dana.reyes@" gives "Dana Reyes"; "j.smith2@" gives
   "J Smith"; anything that reads like a mailbox rather than a person
   ("info@", "sales-team-4@") gives "" and the account is created with a blank
   first name, which the register endpoints already accept. A blank name is a
   small, obvious thing for someone to fix on their profile. A wrong name
   asserted onto a public posting is not. */

// Mailboxes that belong to a function, not a person. Guessing a name from one
// of these produces "Info" on a public profile.
const ROLE_MAILBOXES = new Set([
  "info", "hello", "contact", "admin", "support", "sales", "team", "office",
  "help", "billing", "accounts", "hr", "jobs", "careers", "noreply",
  "no-reply", "mail", "email", "me", "test",
]);

const clean = (part) =>
  part
    .replace(/\+.*$/, "")        // plus-addressing: dana+sift@ is still Dana
    .replace(/\d+/g, "")         // jsmith2 -> jsmith
    .replace(/[._\-]+/g, " ")    // the three separators people actually use
    .replace(/\s+/g, " ")
    .trim();

/**
 * Best-effort personal name from an email address, or "" when there isn't one
 * worth asserting. Never throws; safe on junk input.
 */
export function nameFromEmail(email) {
  const local = String(email || "").split("@")[0] || "";
  if (!local) return "";

  const words = clean(local).split(" ").filter(Boolean);
  if (!words.length) return "";
  if (words.length === 1 && ROLE_MAILBOXES.has(words[0].toLowerCase())) return "";
  // Four "words" out of a local part is an identifier, not a name.
  if (words.length > 3) return "";

  return words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ")
    .slice(0, 150);
}
